/**
 * Headline copy and ladder rows for trade session stats (tape profile).
 */

export type TradeSessionSideMix = {
  buy_count?: number;
  sell_count?: number;
  neutral_count?: number;
  buy_volume?: number;
  sell_volume?: number;
  neutral_volume?: number;
  buy_trade_pct?: number | null;
  sell_trade_pct?: number | null;
  neutral_trade_pct?: number | null;
  buy_volume_pct?: number | null;
  sell_volume_pct?: number | null;
  neutral_volume_pct?: number | null;
  classified_buy_pct_of_classified_trades?: number | null;
  classified_sell_pct_of_classified_trades?: number | null;
};

export type TradeSessionVsRef = {
  above_vwap_trade_pct?: number | null;
  below_vwap_trade_pct?: number | null;
  above_vwap_volume_pct?: number | null;
  below_vwap_volume_pct?: number | null;
  above_open_trade_pct?: number | null;
  below_open_trade_pct?: number | null;
  above_close_trade_pct?: number | null;
  below_close_trade_pct?: number | null;
};

export type TradeSessionPriceLevel = {
  price: number;
  trade_count?: number;
  volume?: number;
};

export type TradeSessionThreshold = {
  price: number;
  pct_trades_at_or_above?: number | null;
  pct_trades_at_or_below?: number | null;
  pct_volume_at_or_above?: number | null;
  pct_volume_at_or_below?: number | null;
};

export type TradeSessionRegularHours = {
  total_trades?: number;
  total_volume?: number;
  total_notional_usd?: number;
  vwap?: number | null;
  open?: number | null;
  high?: number | null;
  low?: number | null;
  close?: number | null;
  side_mix?: TradeSessionSideMix;
  vs_vwap?: TradeSessionVsRef;
  vs_open?: TradeSessionVsRef;
  vs_close?: TradeSessionVsRef;
  top_prices_by_trade_count?: TradeSessionPriceLevel[];
  top_prices_by_volume?: TradeSessionPriceLevel[];
  price_thresholds?: TradeSessionThreshold[];
  price_histogram?: {
    bucket_cents?: number;
    unique_price_levels?: number;
    by_volume?: Record<string, number>;
    by_trade_count?: Record<string, number>;
  };
};

export type TradeSessionStatsResponse = {
  success: boolean;
  symbol: string;
  date: string;
  deduped_trade_count?: number;
  regular_hours?: TradeSessionRegularHours;
  extended?: TradeSessionRegularHours;
  error?: string;
};

export type TapeProfileChip = {
  label: string;
  value: string;
  hint?: string;
};

export type TapeProfileLadderRow = {
  price: number;
  volume: number;
  pctOfSessionVolume: number;
  barPct: number;
  aboveVwap: boolean;
  markers: string[];
  isContextPin?: boolean;
};

export type TapeProfileLadderResult = {
  rows: TapeProfileLadderRow[];
  topRowCount: number;
  shownVolume: number;
  shownVolumePct: number;
  otherVolume: number;
  otherVolumePct: number;
  otherPriceLevelCount: number;
  totalVolume: number;
  uniquePriceLevels: number;
};

export type TapeProfileHistogramBarRow = {
  kind: 'bar';
  price: number;
  volume: number;
  tradeCount: number;
  pctOfSessionVolume: number;
  pctOfSessionTrades: number;
  barPct: number;
  aboveVwap: boolean;
};

export type TapeProfileHistogramMarkerRow = {
  kind: 'marker';
  label: 'High' | 'Open' | 'Close' | 'Low';
  price: number;
};

export type TapeProfileHistogramRow =
  | TapeProfileHistogramBarRow
  | TapeProfileHistogramMarkerRow;

export type TapeProfileHistogramResult = {
  rows: TapeProfileHistogramRow[];
  totalVolume: number;
  totalTrades: number;
  levelCount: number;
  maxVolume: number;
};

export type TapeProfileHistogramSort = 'price-desc' | 'volume-desc' | 'prints-desc';

export const TAPE_PROFILE_HISTOGRAM_SORT_OPTIONS: Array<{
  id: TapeProfileHistogramSort;
  label: string;
}> = [
  { id: 'price-desc', label: 'Price ↓' },
  { id: 'volume-desc', label: 'Volume ↓' },
  { id: 'prints-desc', label: 'Prints ↓' },
];

function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v == null || Number.isNaN(v)) return 'N/A';
  return `${v.toFixed(digits)}%`;
}

function fmtPrice(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return 'N/A';
  return `$${v.toFixed(2)}`;
}

function fmtCompact(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return 'N/A';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function nearestThreshold(
  thresholds: TradeSessionThreshold[] | undefined,
  target: number,
): TradeSessionThreshold | undefined {
  if (!thresholds?.length) return undefined;
  return thresholds.reduce((best, row) =>
    Math.abs(row.price - target) < Math.abs(best.price - target) ? row : best,
  );
}

export function buildTapeProfileChips(rth: TradeSessionRegularHours): TapeProfileChip[] {
  const chips: TapeProfileChip[] = [];
  const vwap = rth.vwap;
  const vsVwap = rth.vs_vwap;

  if (vsVwap?.above_vwap_volume_pct != null && vwap != null) {
    chips.push({
      label: 'Above VWAP',
      value: fmtPct(vsVwap.above_vwap_volume_pct),
      hint: `VWAP ${fmtPrice(vwap)}`,
    });
  }

  const side = rth.side_mix;
  if (
    side?.classified_buy_pct_of_classified_trades != null &&
    side?.classified_sell_pct_of_classified_trades != null
  ) {
    chips.push({
      label: 'Classified tilt',
      value: `${fmtPct(side.classified_buy_pct_of_classified_trades, 1)} buy`,
      hint: `${fmtPct(side.classified_sell_pct_of_classified_trades, 1)} sell (B/A prints only)`,
    });
  }

  const topVol = rth.top_prices_by_volume?.[0];
  if (topVol?.price != null && topVol.volume != null && rth.total_volume) {
    const pct = (topVol.volume / rth.total_volume) * 100;
    chips.push({
      label: 'Peak volume price',
      value: fmtPrice(topVol.price),
      hint: `${fmtPct(pct, 1)} of session volume`,
    });
  }

  return chips;
}

/**
 * Full ET calendar-day session (pre-market + RTH + after-hours).
 * Matches backend tape imbalance: prefer ``extended``, fall back to ``regular_hours``.
 */
export function fullTradingDaySessionFromStats(
  stats: Pick<TradeSessionStatsResponse, 'regular_hours' | 'extended'> | null | undefined,
): TradeSessionRegularHours | null {
  const extended = stats?.extended;
  if (extended && (extended.total_trades ?? 0) > 0) return extended;
  const rth = stats?.regular_hours;
  if (rth && (rth.total_trades ?? 0) > 0) return rth;
  return null;
}

/** Buy/sell/neutral share totals from a session stats block (RTH or full day). */
export function tapeBuySellFromRegularHours(
  rth: TradeSessionRegularHours | null | undefined,
): { buyShares: number; sellShares: number; neutralShares: number } | null {
  const side = rth?.side_mix;
  if (!side) return null;
  const buyShares = Math.max(0, Math.round(Number(side.buy_volume ?? 0)));
  const sellShares = Math.max(0, Math.round(Number(side.sell_volume ?? 0)));
  const neutralShares = Math.max(0, Math.round(Number(side.neutral_volume ?? 0)));
  const total = buyShares + sellShares + neutralShares;
  if (total <= 0) return null;
  return { buyShares, sellShares, neutralShares };
}

export function buildTapeProfileSummary(rth: TradeSessionRegularHours): string {
  const parts: string[] = [];
  const trades = rth.total_trades;
  const vol = rth.total_volume;
  if (trades != null && vol != null) {
    parts.push(`${fmtCompact(trades)} prints · ${fmtCompact(vol)} shares`);
  }

  if (rth.low != null && rth.high != null) {
    parts.push(`range ${fmtPrice(rth.low)}–${fmtPrice(rth.high)}`);
  }

  if (rth.open != null && rth.close != null && rth.open > 0) {
    const chg = ((rth.close - rth.open) / rth.open) * 100;
    const sign = chg >= 0 ? '+' : '';
    parts.push(`closed ${sign}${chg.toFixed(2)}% from open`);
  }

  const topVol = rth.top_prices_by_volume?.[0];
  if (topVol?.price != null) {
    parts.push(`most volume at ${fmtPrice(topVol.price)}`);
  }

  if (rth.high != null && rth.close != null && rth.high > rth.close * 1.01) {
    const highTh = nearestThreshold(rth.price_thresholds, rth.high);
    const aboveHigh =
      highTh?.pct_volume_at_or_below != null
        ? (100 - highTh.pct_volume_at_or_below).toFixed(1)
        : null;
    if (aboveHigh != null && parseFloat(aboveHigh) < 5) {
      parts.push(`spike to ${fmtPrice(rth.high)} was thin (${aboveHigh}% of volume near highs)`);
    }
  }

  return parts.join(' · ');
}

function markerLabels(price: number, rth: TradeSessionRegularHours, tolerance = 0.02): string[] {
  const tags: string[] = [];
  const check = (label: string, ref: number | null | undefined) => {
    if (ref == null) return;
    if (Math.abs(price - ref) <= tolerance) tags.push(label);
  };
  check('Open', rth.open);
  check('VWAP', rth.vwap);
  check('Close', rth.close);
  check('High', rth.high);
  check('Low', rth.low);
  return tags;
}

function volumeAtCent(
  hist: TradeSessionRegularHours['price_histogram'],
  price: number,
): number {
  const key = String(Math.round(price * 100));
  return hist?.by_volume?.[key] ?? 0;
}

export function buildTapeProfileLadder(
  rth: TradeSessionRegularHours,
  maxRows = 14,
): TapeProfileLadderResult | null {
  const byVol = rth.top_prices_by_volume ?? [];
  const totalVol = rth.total_volume ?? 0;
  if (!byVol.length || totalVol <= 0) return null;

  const uniquePriceLevels =
    rth.price_histogram?.unique_price_levels ?? byVol.length;

  const seen = new Set<number>();
  const topRows: TapeProfileLadderRow[] = [];

  const push = (
    price: number,
    volume: number,
    opts?: { isContextPin?: boolean },
  ) => {
    const key = Math.round(price * 100);
    if (seen.has(key) || volume <= 0) return;
    seen.add(key);
    topRows.push({
      price,
      volume,
      pctOfSessionVolume: (volume / totalVol) * 100,
      barPct: 0,
      aboveVwap: rth.vwap != null ? price >= rth.vwap : false,
      markers: markerLabels(price, rth),
      isContextPin: opts?.isContextPin,
    });
  };

  for (const row of byVol.slice(0, maxRows)) {
    if (row.price != null && row.volume != null) push(row.price, row.volume);
  }

  if (rth.high != null && !seen.has(Math.round(rth.high * 100))) {
    const highVol = volumeAtCent(rth.price_histogram, rth.high);
    if (highVol > 0) {
      push(rth.high, highVol);
    } else {
      push(rth.high, 1, { isContextPin: true });
    }
  }

  const shownVolume = topRows
    .filter((r) => !r.isContextPin)
    .reduce((s, r) => s + r.volume, 0);
  const otherVolume = Math.max(0, totalVol - shownVolume);
  const otherPriceLevelCount = Math.max(0, uniquePriceLevels - topRows.filter((r) => !r.isContextPin).length);

  const rows = [...topRows];
  if (otherVolume > 0) {
    rows.push({
      price: NaN,
      volume: otherVolume,
      pctOfSessionVolume: (otherVolume / totalVol) * 100,
      barPct: 0,
      aboveVwap: false,
      markers: [],
      isContextPin: false,
    });
  }

  rows.sort((a, b) => {
    if (Number.isNaN(a.price)) return 1;
    if (Number.isNaN(b.price)) return -1;
    return b.price - a.price;
  });

  const maxBarVol = Math.max(
    ...rows.filter((r) => !Number.isNaN(r.price)).map((r) => r.volume),
    1,
  );
  const scaled = rows.map((r) => ({
    ...r,
    barPct: Number.isNaN(r.price)
      ? (r.volume / totalVol) * 100
      : (r.volume / maxBarVol) * 100,
  }));

  return {
    rows: scaled,
    topRowCount: topRows.filter((r) => !r.isContextPin).length,
    shownVolume,
    shownVolumePct: (shownVolume / totalVol) * 100,
    otherVolume,
    otherVolumePct: (otherVolume / totalVol) * 100,
    otherPriceLevelCount,
    totalVolume: totalVol,
    uniquePriceLevels,
  };
}

/** @deprecated Use buildTapeProfileLadder */
export function buildTapeProfileLadderRows(
  rth: TradeSessionRegularHours,
  maxRows = 14,
): TapeProfileLadderRow[] {
  return buildTapeProfileLadder(rth, maxRows)?.rows ?? [];
}

const OHLC_MARKER_ORDER: Array<'High' | 'Open' | 'Close' | 'Low'> = [
  'High',
  'Open',
  'Close',
  'Low',
];

function ohlcMarkers(
  rth: TradeSessionRegularHours,
): Array<{ label: 'High' | 'Open' | 'Close' | 'Low'; price: number }> {
  const out: Array<{ label: 'High' | 'Open' | 'Close' | 'Low'; price: number }> = [];
  for (const label of OHLC_MARKER_ORDER) {
    const price =
      label === 'High'
        ? rth.high
        : label === 'Open'
          ? rth.open
          : label === 'Close'
            ? rth.close
            : rth.low;
    if (price != null && !Number.isNaN(price)) {
      out.push({ label, price });
    }
  }
  return out;
}

/** Cent-level session histogram for prices with trades, OHLC markers between bars. */
export function buildTapeProfileHistogram(
  rth: TradeSessionRegularHours,
): TapeProfileHistogramResult | null {
  const hist = rth.price_histogram;
  const totalVol = rth.total_volume ?? 0;
  const totalTrades = rth.total_trades ?? 0;
  if (!hist?.by_volume || totalVol <= 0 || totalTrades <= 0) return null;

  const bars: TapeProfileHistogramBarRow[] = [];
  let maxVol = 0;
  const centLevels = Object.keys(hist.by_volume)
    .map((key) => Number.parseInt(key, 10))
    .filter((cent) => Number.isFinite(cent))
    .sort((a, b) => b - a);

  for (const cent of centLevels) {
    const volume = hist.by_volume[String(cent)] ?? 0;
    if (volume <= 0) continue;
    const tradeCount = hist.by_trade_count?.[String(cent)] ?? 0;
    if (volume > maxVol) maxVol = volume;
    bars.push({
      kind: 'bar',
      price: cent / 100,
      volume,
      tradeCount,
      pctOfSessionVolume: (volume / totalVol) * 100,
      pctOfSessionTrades: (tradeCount / totalTrades) * 100,
      barPct: 0,
      aboveVwap: rth.vwap != null ? cent / 100 >= rth.vwap : false,
    });
  }

  if (!bars.length) return null;

  type Sortable = { sortPrice: number; row: TapeProfileHistogramRow; markerRank: number };
  const sortables: Sortable[] = bars.map((row) => ({
    sortPrice: row.price,
    row,
    markerRank: 1,
  }));

  for (const marker of ohlcMarkers(rth)) {
    sortables.push({
      sortPrice: marker.price,
      row: { kind: 'marker', label: marker.label, price: marker.price },
      markerRank: 0,
    });
  }

  sortables.sort((a, b) => {
    if (b.sortPrice !== a.sortPrice) return b.sortPrice - a.sortPrice;
    return a.markerRank - b.markerRank;
  });

  const maxBarVol = Math.max(maxVol, 1);
  const rows = sortables.map(({ row }) =>
    row.kind === 'bar'
      ? {
          ...row,
          barPct: row.volume > 0 ? (row.volume / maxBarVol) * 100 : 0,
        }
      : row,
  );

  return {
    rows,
    totalVolume: totalVol,
    totalTrades,
    levelCount: bars.length,
    maxVolume: maxVol,
  };
}

export function sortTapeProfileHistogramRows(
  rows: TapeProfileHistogramRow[],
  sort: TapeProfileHistogramSort,
): TapeProfileHistogramRow[] {
  if (sort === 'price-desc') return rows;

  const bars = rows.filter((row): row is TapeProfileHistogramBarRow => row.kind === 'bar');
  if (sort === 'volume-desc') {
    return [...bars].sort((a, b) => b.volume - a.volume || b.price - a.price);
  }
  return [...bars].sort((a, b) => b.tradeCount - a.tradeCount || b.price - a.price);
}

export function buildKeyThresholdRows(
  rth: TradeSessionRegularHours,
): Array<{ label: string; price: number; pctVolumeAbove: number | null }> {
  const thresholds = rth.price_thresholds ?? [];
  if (!thresholds.length) return [];

  const targets: Array<{ label: string; price: number }> = [];
  if (rth.open != null) targets.push({ label: 'Open', price: rth.open });
  if (rth.vwap != null) targets.push({ label: 'VWAP', price: rth.vwap });
  if (rth.close != null) targets.push({ label: 'Close', price: rth.close });
  if (rth.high != null) targets.push({ label: 'High', price: rth.high });

  const lo = Math.floor(rth.low ?? thresholds[0].price);
  const hi = Math.ceil(rth.high ?? thresholds[thresholds.length - 1].price);
  for (let d = lo; d <= hi; d += 1) {
    targets.push({ label: `$${d}`, price: d });
  }

  const out: Array<{ label: string; price: number; pctVolumeAbove: number | null }> = [];
  const usedPrices = new Set<number>();
  for (const t of targets) {
    const row = nearestThreshold(thresholds, t.price);
    if (!row) continue;
    const key = Math.round(row.price * 100);
    if (usedPrices.has(key)) continue;
    usedPrices.add(key);
    out.push({
      label: t.label,
      price: row.price,
      pctVolumeAbove: row.pct_volume_at_or_above ?? null,
    });
    if (out.length >= 7) break;
  }
  return out;
}

export { fmtPct, fmtPrice, fmtCompact };
